import React, { useEffect, useState, useCallback } from 'react';
import './ImageModal.css';
import { backdropClose } from './backdrop';

// Fullscreen viewer for a field's image gallery: ←/→ or on-screen chevrons to
// move between images, a numeric "i / n" counter, ESC/backdrop to close.
const ImageModal = ({ images, startIndex = 0, onClose }) => {
  const count = images.length;
  const [index, setIndex] = useState(
    Math.min(Math.max(startIndex, 0), count - 1)
  );

  const goPrev = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);
  const goNext = useCallback(
    () => setIndex((i) => Math.min(count - 1, i + 1)),
    [count]
  );

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
    };

    window.addEventListener('keydown', handleKeyDown);

    // Prevent scrolling of the background
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto';
    };
  }, [onClose, goPrev, goNext]);

  if (count === 0) return null;

  return (
    <div className="modal-backdrop" {...backdropClose(onClose)}>
      <div className="modal-content">
        <button className="modal-close-button" onClick={onClose}>×</button>
        {count > 1 && (
          <div className="modal-counter">{index + 1} / {count}</div>
        )}
        <div className="modal-body">
          {count > 1 && (
            <button
              className="modal-nav-button modal-nav-prev"
              onClick={goPrev}
              disabled={index === 0}
              title="Previous image"
            >
              ‹
            </button>
          )}
          <img
            src={images[index].url}
            alt={images[index].caption || `Full size ${index + 1} of ${count}`}
            className="modal-image"
          />
          {count > 1 && (
            <button
              className="modal-nav-button modal-nav-next"
              onClick={goNext}
              disabled={index === count - 1}
              title="Next image"
            >
              ›
            </button>
          )}
        </div>
        {images[index].caption && (
          <div className="modal-caption">{images[index].caption}</div>
        )}
      </div>
    </div>
  );
};

export default ImageModal;
